import type p5 from 'p5';
import type { PoseData, VisualConfig, PoseLandmark, PersonID } from '../../../types';

/**
 * Position in trail history
 */
interface TrailPosition {
  x: number;
  y: number;
  visibility: number;
}

/**
 * Renders motion trails following body landmarks.
 * Supports both single-person and multi-person modes with per-person trail buffers.
 */
export class TrailRenderer {
  // Single-person trail history (legacy)
  private trailHistory: Map<string, TrailPosition[]>;
  
  // Multi-person trail history: Map<PersonID, Map<landmarkName, TrailPosition[]>>
  private personTrailHistory: Map<PersonID, Map<string, TrailPosition[]>>;
  
  // Landmarks to track for trails
  private readonly trackedLandmarks = [
    'leftWrist',
    'rightWrist',
    'leftElbow',
    'rightElbow',
    'leftShoulder',
    'rightShoulder',
    'leftHip',
    'rightHip',
    'leftKnee',
    'rightKnee',
    'leftAnkle',
    'rightAnkle',
  ];

  constructor() {
    this.trailHistory = new Map();
    this.personTrailHistory = new Map();
    
    // Initialize trail buffers for each tracked landmark (single-person mode)
    for (const landmark of this.trackedLandmarks) {
      this.trailHistory.set(landmark, []);
    }
  }

  /**
   * Initialize trail buffers for a new person.
   * Creates empty trail arrays for all tracked landmarks.
   * 
   * @param personId Person identifier
   * @private
   */
  private initializePersonTrails(personId: PersonID): void {
    const trails = new Map<string, TrailPosition[]>();
    for (const landmark of this.trackedLandmarks) {
      trails.set(landmark, []);
    }
    this.personTrailHistory.set(personId, trails);
  }

  /**
   * Update trail buffers with new pose data (single-person mode).
   * 
   * @deprecated Use updateTrailsForPerson for multi-person support
   */
  updateTrails(pose: PoseData, trailLength: number): void {
    if (!pose.detected) {
      // Clear trails when no pose detected
      this.clearTrails();
      return;
    }

    const maxLength = Math.max(1, trailLength);

    for (const landmarkName of this.trackedLandmarks) {
      const landmark = pose.landmarks[landmarkName as keyof typeof pose.landmarks] as PoseLandmark;
      
      if (!landmark) {
        continue;
      }

      const trail = this.trailHistory.get(landmarkName);
      if (!trail) {
        continue;
      }

      // Add new position to trail
      trail.push({
        x: landmark.x,
        y: landmark.y,
        visibility: landmark.visibility,
      });

      // Remove old positions if buffer exceeds max length
      while (trail.length > maxLength) {
        trail.shift();
      }
    }
  }

  /**
   * Update trail buffers for a specific person (multi-person mode).
   * Maintains separate trail history per person.
   * 
   * @param personId Person identifier
   * @param pose Pose data for this person
   * @param trailLength Maximum trail length
   */
  updateTrailsForPerson(personId: PersonID, pose: PoseData, trailLength: number): void {
    if (!pose.detected) {
      return;
    }

    // Initialize trails for this person if not exists
    if (!this.personTrailHistory.has(personId)) {
      this.initializePersonTrails(personId);
    }

    const personTrails = this.personTrailHistory.get(personId);
    if (!personTrails) {
      return;
    }

    const maxLength = Math.max(1, trailLength);

    for (const landmarkName of this.trackedLandmarks) {
      const landmark = pose.landmarks[landmarkName as keyof typeof pose.landmarks] as PoseLandmark;
      
      if (!landmark) {
        continue;
      }

      const trail = personTrails.get(landmarkName);
      if (!trail) {
        continue;
      }

      // Add new position to trail
      trail.push({
        x: landmark.x,
        y: landmark.y,
        visibility: landmark.visibility,
      });

      // Remove old positions if buffer exceeds max length
      while (trail.length > maxLength) {
        trail.shift();
      }
    }
  }

  /**
   * Clear trail buffers for a specific person.
   * Called when person exits frame.
   * 
   * @param personId Person identifier
   */
  clearPersonTrails(personId: PersonID): void {
    this.personTrailHistory.delete(personId);
  }

  /**
   * Clear all trail data (single-person mode).
   */
  clearTrails(): void {
    for (const trail of this.trailHistory.values()) {
      trail.length = 0;
    }
  }

  /**
   * Render trails for all tracked landmarks (single-person mode).
   * 
   * @deprecated Use renderForPerson for multi-person support
   */
  render(p: p5, config: VisualConfig): void {
    this.renderTrails(p, config, this.trailHistory);
  }

  /**
   * Render trails for a specific person (multi-person mode).
   * 
   * @param p p5 instance
   * @param personId Person identifier
   * @param config Visual configuration for this person
   */
  renderForPerson(p: p5, personId: PersonID, config: VisualConfig): void {
    const personTrails = this.personTrailHistory.get(personId);
    if (!personTrails) {
      return;
    }
    this.renderTrails(p, config, personTrails);
  }

  /**
   * Internal method to render trails from a trail history map.
   * 
   * @param p p5 instance
   * @param config Visual configuration
   * @param trails Trail history map
   * @private
   */
  private renderTrails(p: p5, config: VisualConfig, trails: Map<string, TrailPosition[]>): void {
    const width = p.width;
    const height = p.height;

    // Set drawing style with enhanced stroke weight
    p.strokeWeight(config.stickFigureThickness * 1.2); // Thicker for better visibility
    p.noFill();

    for (const trail of trails.values()) {
      if (trail.length < 2) {
        continue; // Need at least 2 points to draw a line
      }

      // Draw trail as connected line segments with fading opacity
      for (let i = 0; i < trail.length - 1; i++) {
        const current = trail[i];
        const next = trail[i + 1];

        // Skip if either position has low visibility or is undefined
        if (!current || !next || current.visibility < 0.5 || next.visibility < 0.5) {
          continue;
        }

        // Calculate opacity fade: newest (end of array) is opaque, oldest (start) is transparent
        // Use exponential fade for more vibrant recent trails
        const progress = i / (trail.length - 1);
        const exponentialProgress = Math.pow(progress, 0.7); // Exponential curve for better fade
        const opacity = exponentialProgress * config.trailOpacity * 0.9; // Increased base opacity

        // Apply color with fading opacity
        p.stroke(
          config.primaryColor.r,
          config.primaryColor.g,
          config.primaryColor.b,
          opacity * 255
        );

        // Convert normalized coordinates to screen coordinates
        const x1 = current.x * width;
        const y1 = current.y * height;
        const x2 = next.x * width;
        const y2 = next.y * height;

        p.line(x1, y1, x2, y2);
      }
      
      // Draw glow effect on the most recent trail segment for emphasis
      if (trail.length >= 2) {
        const secondLast = trail[trail.length - 2];
        const last = trail[trail.length - 1];
        
        if (secondLast && last && secondLast.visibility >= 0.5 && last.visibility >= 0.5) {
          const x1 = secondLast.x * width;
          const y1 = secondLast.y * height;
          const x2 = last.x * width;
          const y2 = last.y * height;
          
          // Draw glow layer
          p.strokeWeight(config.stickFigureThickness * 2);
          p.stroke(
            config.primaryColor.r,
            config.primaryColor.g,
            config.primaryColor.b,
            config.trailOpacity * 100 // Subtle glow
          );
          p.line(x1, y1, x2, y2);
          
          // Draw bright core
          p.strokeWeight(config.stickFigureThickness * 1.2);
          p.stroke(
            config.primaryColor.r,
            config.primaryColor.g,
            config.primaryColor.b,
            config.trailOpacity * 255
          );
          p.line(x1, y1, x2, y2);
        }
      }
    }
  }

  /**
   * Get current trail length for a specific landmark (for testing, single-person mode).
   */
  getTrailLength(landmarkName: string): number {
    const trail = this.trailHistory.get(landmarkName);
    return trail ? trail.length : 0;
  }

  /**
   * Get maximum trail length across all landmarks (for testing, single-person mode).
   */
  getMaxTrailLength(): number {
    let max = 0;
    for (const trail of this.trailHistory.values()) {
      max = Math.max(max, trail.length);
    }
    return max;
  }

  /**
   * Get current trail length for a specific person and landmark (for testing, multi-person mode).
   */
  getPersonTrailLength(personId: PersonID, landmarkName: string): number {
    const personTrails = this.personTrailHistory.get(personId);
    if (!personTrails) {
      return 0;
    }
    const trail = personTrails.get(landmarkName);
    return trail ? trail.length : 0;
  }

  /**
   * Get maximum trail length for a specific person (for testing, multi-person mode).
   */
  getPersonMaxTrailLength(personId: PersonID): number {
    const personTrails = this.personTrailHistory.get(personId);
    if (!personTrails) {
      return 0;
    }
    let max = 0;
    for (const trail of personTrails.values()) {
      max = Math.max(max, trail.length);
    }
    return max;
  }
}
